import React from 'react';

/**
 * Sidebar icon family.
 *
 * One coherent outline set so every menu entry reads as its own thing - the
 * previous sidebar reused GameManagementIcon for six different destinations
 * and TransactionsIcon for three. Same props as the older icon components
 * (width / height / color) so they drop straight into the existing markup.
 */
export interface NavIconProps {
  width?: number;
  height?: number;
  color?: string;
}

const Svg: React.FC<NavIconProps & { children: React.ReactNode }> = ({
  width = 20,
  height = 20,
  color = 'white',
  children,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    {children}
  </svg>
);

/* Dashboard ------------------------------------------------------------- */
export const NavHomeIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M3 10.2 12 3.5l9 6.7" />
    <path d="M5.2 9v10.5h13.6V9" />
    <path d="M9.6 19.5v-6h4.8v6" />
  </Svg>
);

/* People ---------------------------------------------------------------- */
export const NavUsersIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <circle cx="9.2" cy="8.4" r="3.2" />
    <path d="M3.2 19.4c0-3.1 2.7-5.2 6-5.2s6 2.1 6 5.2" />
    <path d="M16.4 5.6a3 3 0 0 1 0 5.6" />
    <path d="M17.6 14.6c1.9.6 3.2 2.1 3.2 4.1" />
  </Svg>
);

export const NavPlayerIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.8 20c0-3.5 3.2-5.8 7.2-5.8s7.2 2.3 7.2 5.8" />
  </Svg>
);

export const NavAgentIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="7.6" r="3.2" />
    <path d="M5.6 20c0-3.3 2.9-5.5 6.4-5.5 1.1 0 2.2.2 3.1.6" />
    <path d="m16.4 17.9 1.5 1.5 3.1-3.4" />
  </Svg>
);

/* Hierarchy ------------------------------------------------------------- */
export const NavDistributorIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <rect x="9.2" y="3" width="5.6" height="4.4" rx="1.2" />
    <rect x="3" y="16.6" width="5.6" height="4.4" rx="1.2" />
    <rect x="15.4" y="16.6" width="5.6" height="4.4" rx="1.2" />
    <path d="M12 7.4v4.2" />
    <path d="M5.8 16.6v-2.4a2.6 2.6 0 0 1 2.6-2.6h7.2a2.6 2.6 0 0 1 2.6 2.6v2.4" />
  </Svg>
);

export const NavSubDistributorIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <rect x="3.2" y="3.2" width="5" height="4" rx="1.1" />
    <rect x="13.8" y="9.8" width="7" height="4" rx="1.1" />
    <rect x="13.8" y="16.4" width="7" height="4" rx="1.1" />
    <path d="M5.7 7.2v10a1.6 1.6 0 0 0 1.6 1.6h6.5" />
    <path d="M5.7 11.8h8.1" />
  </Svg>
);

export const NavShopIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M3.6 9.2 5 4.4h14L20.4 9.2" />
    <path d="M3.6 9.2a2.1 2.1 0 0 0 4.2 0 2.1 2.1 0 0 0 4.2 0 2.1 2.1 0 0 0 4.2 0 2.1 2.1 0 0 0 4.2 0" />
    <path d="M5.2 11v8.6h13.6V11" />
    <path d="M10 19.6v-4.4h4v4.4" />
  </Svg>
);

/* Money ----------------------------------------------------------------- */
export const NavTransactionsIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M7.4 4.6v13" />
    <path d="m4 8 3.4-3.4L10.8 8" />
    <path d="M16.6 19.4v-13" />
    <path d="M13.2 16l3.4 3.4L20 16" />
  </Svg>
);

export const NavOnlineTransactionsIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.6" />
    <path d="M3.6 9.8h16.8" />
    <path d="M3.6 14.2h16.8" />
    <path d="M12 3.4c2.2 2.4 3.3 5.3 3.3 8.6s-1.1 6.2-3.3 8.6c-2.2-2.4-3.3-5.3-3.3-8.6s1.1-6.2 3.3-8.6Z" />
  </Svg>
);

export const NavProviderBalanceIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M3.4 8.2A2.2 2.2 0 0 1 5.6 6h11.8a2.2 2.2 0 0 1 2.2 2.2v9.4a2.2 2.2 0 0 1-2.2 2.2H5.6a2.2 2.2 0 0 1-2.2-2.2Z" />
    <path d="M3.6 8.4 15.6 4.2l1.2 1.7" />
    <path d="M20.6 11.6h-3.4a1.7 1.7 0 0 0 0 3.4h3.4" />
  </Svg>
);

export const NavWithdrawReviewIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M8 4.6H6.4a1.8 1.8 0 0 0-1.8 1.8v12.2a1.8 1.8 0 0 0 1.8 1.8h11.2a1.8 1.8 0 0 0 1.8-1.8V6.4a1.8 1.8 0 0 0-1.8-1.8H16" />
    <rect x="8" y="2.8" width="8" height="3.6" rx="1.2" />
    <path d="m8.8 13.4 2 2 4.4-4.6" />
  </Svg>
);

/* Games ----------------------------------------------------------------- */
export const NavGameHistoryIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M3.6 12a8.4 8.4 0 1 0 2.6-6.1" />
    <path d="M3.4 4.4v4.2h4.2" />
    <path d="M12 8.2V12l2.8 1.8" />
  </Svg>
);

export const NavGameManagementIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M7.6 7.4h8.8a4.4 4.4 0 0 1 4.3 3.5l.9 4.4a2.3 2.3 0 0 1-4.2 1.7l-1.2-1.9H7.8l-1.2 1.9a2.3 2.3 0 0 1-4.2-1.7l.9-4.4a4.4 4.4 0 0 1 4.3-3.5Z" />
    <path d="M7.4 10.4v3" />
    <path d="M5.9 11.9h3" />
    <path d="M15.6 11.4h.01" />
    <path d="M17.9 13.4h.01" />
  </Svg>
);

export const NavRaffleIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M3.4 8.6V6.8a1.4 1.4 0 0 1 1.4-1.4h14.4a1.4 1.4 0 0 1 1.4 1.4v1.8a2.4 2.4 0 0 0 0 4.8v1.8a1.4 1.4 0 0 1-1.4 1.4H4.8a1.4 1.4 0 0 1-1.4-1.4v-1.8a2.4 2.4 0 0 0 0-4.8Z" />
    <path d="M9.4 8.8v4.4" />
    <path d="M14.2 8.8v4.4" />
  </Svg>
);

export const NavPromoIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M4 9.6v4.2a1.4 1.4 0 0 0 1.4 1.4h1.9l5.6 3.4V5.8L7.3 9.2H5.4A1.4 1.4 0 0 0 4 10.6Z" />
    <path d="M16.4 9.4a3.6 3.6 0 0 1 0 5.2" />
    <path d="M18.8 7a7 7 0 0 1 0 10" />
    <path d="M7.3 15.2v3.4" />
  </Svg>
);

/* Machines -------------------------------------------------------------- */
export const NavAtmIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <rect x="4.4" y="3.2" width="15.2" height="17.6" rx="2.2" />
    <rect x="7.4" y="6.4" width="9.2" height="5.2" rx="1.2" />
    <path d="M7.6 14.6h4.2" />
    <path d="M7.6 17.4h2.6" />
    <path d="M14.6 16h2.2" />
  </Svg>
);

/* System ---------------------------------------------------------------- */
export const NavConfigurationsIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M4 7h6.4" />
    <path d="M14.2 7H20" />
    <circle cx="12.3" cy="7" r="1.9" />
    <path d="M4 17h4.4" />
    <path d="M12.2 17H20" />
    <circle cx="10.3" cy="17" r="1.9" />
  </Svg>
);

export const NavPermissionsIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M12 3.2 5.2 5.8v5.5c0 4.1 2.8 7.6 6.8 9.5 4-1.9 6.8-5.4 6.8-9.5V5.8Z" />
    <path d="m9.1 11.8 2.1 2.2 3.7-4" />
  </Svg>
);

export const NavNotificationsIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M6.4 10.2a5.6 5.6 0 0 1 11.2 0c0 4 1.4 5.6 1.4 5.6H5c0-.1 1.4-1.6 1.4-5.6Z" />
    <path d="M10.2 18.4a2 2 0 0 0 3.6 0" />
    <path d="M12 4.6V3.2" />
  </Svg>
);

export const NavSupportIcon: React.FC<NavIconProps> = (p) => (
  <Svg {...p}>
    <path d="M4.4 14.4v-2.2a7.6 7.6 0 0 1 15.2 0v2.2" />
    <rect x="2.8" y="13.2" width="3.6" height="5.4" rx="1.6" />
    <rect x="17.6" y="13.2" width="3.6" height="5.4" rx="1.6" />
    <path d="M19.4 18.6v.6a2.2 2.2 0 0 1-2.2 2.2H13" />
  </Svg>
);
